/**
 * @file    navbar.component.ts
 * @brief   Provides the navbar for the app.
 *
 * Copyright (c) Bsquare Corporation 2018. All rights reserved.
 */
import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ConfirmationModalComponent, ModalService, BsCommon } from '@bsquare/angular-common';
import { AboutModalComponent } from 'app/modals/about-modal/about-modal.component';
import { AuthService, DEFAULT_ROUTE, UserInfo } from 'app/services/auth.service';
import { UserService } from 'app/services/user-management/user.service';
import { FormsModule } from '@angular/forms';

/**
 * Provides the navbar for the app.
 */
@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  imports: [RouterLink, FormsModule, BsCommon],
})
export class NavbarComponent implements OnInit {
  public authService = inject(AuthService);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  public userInfo?: UserInfo;
  public readonly HOME_LINK = DEFAULT_ROUTE;
  public canViewRoles?: boolean;
  private readonly router = inject(Router);
  public admin = false;
  public searchString?: string;

  public constructor() {
    this.authService.userInfo.subscribe((info) => (this.userInfo = info));
  }

  public async onSearch(): Promise<void> {
    if (this.searchString) {
      await this.router.navigate(['/manage/search', this.searchString]);
    }
  }

  public getAbout(): void {
    this.modalService.invokeModal(AboutModalComponent, { error: 'Failed to open about modal' });
  }

  public ngOnInit(): void {
    this.authService.permissions.subscribe((permissions) => {
      this.admin = permissions.some((permission) => permission.startsWith('Administration'));
    });
  }

  public changePassword(): void {
    this.modalService.invokeModalWithSubmit(
      ConfirmationModalComponent,
      {
        action: () => this.userService.changePassword(),
        success: 'Successfully sent change password request',
        error: 'Failed to send change password request',
      },
      {
        title: 'Change password',
        content: 'An email will be sent with instructions on how to change your password',
      },
    );
  }
}
